import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  InputHTMLAttributes,
} from 'react';
import { IconBaseProps } from 'react-icons';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useField } from '@unform/core';

import { Container } from './styles';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  icon?: React.ComponentType<IconBaseProps>;
}

const Input: React.FC<InputProps> = ({ name, icon: Icon, type, ...rest }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    fieldName,
    defaultValue = '',
    registerField,
    error,
    clearError,
  } = useField(name);

  const [isFocused, setIsFocused] = useState(false);
  const [isFilled, setIsFilled] = useState(!!defaultValue);
  const [showPassword, setShowPassword] = useState(false);

  const isPasswordField = type === 'password';

  let inputType = type;

  if (isPasswordField) {
    inputType = showPassword ? 'text' : 'password';
  }

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [fieldName, registerField]);

  const handleInputFocus = useCallback(() => {
    setIsFocused(true);

    if (clearError) {
      clearError();
    }
  }, [clearError]);

  const handleInputBlur = useCallback(() => {
    setIsFocused(false);
    setIsFilled(!!inputRef.current?.value);
  }, []);

  const handleInputChange = useCallback(() => {
    setIsFilled(!!inputRef.current?.value);

    if (clearError) {
      clearError();
    }
  }, [clearError]);

  const handleTogglePasswordVisibility = useCallback(() => {
    setShowPassword(state => !state);
  }, []);

  return (
    <Container
      isFocused={isFocused}
      isFilled={isFilled}
      hasError={!!error}
      hasPasswordToggle={isPasswordField}
    >
      {Icon && <Icon size={20} />}

      <input
        ref={inputRef}
        defaultValue={defaultValue}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        type={inputType}
        {...rest}
      />

      {isPasswordField && (
        <button
          type="button"
          className="togglePassword"
          onClick={handleTogglePasswordVisibility}
          aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
        >
          {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
        </button>
      )}
    </Container>
  );
};

export default Input;
